// UX pass 2026-10-25 (Val: "The Ride: ATL's Engagements would be 'Reservations'... where in settings would I change that?").
// ONE company setting: "What do you call a booking?" (Admin Center > Company Words). The company saves one word, in the
// singular (orgs.booking_word, migration 256). Every staff screen derives the forms it needs from it, and the same save
// writes the plural to org_settings.bookings_label, the word the client app already reads (BL-407 round 2, migration 194).
// Nothing saved = "Engagement", so every staff screen reads exactly as before until an admin chooses a word.
import { lowerWord, pluralWord } from '../components/events/eventWords';

export const DEFAULT_BOOKING_WORD = 'Engagement';
export const BOOKING_WORD_PATTERN = /^[A-Za-z0-9]([A-Za-z0-9 &'-]{0,22}[A-Za-z0-9])?$/;
export const BOOKING_WORD_RULE = 'Use 1 to 24 letters, numbers, spaces, &, - or \', starting and ending with a letter or number.';

// The presets the admin picks from (singular; the screen shows the plural too).
export const BOOKING_WORD_PRESETS = Object.freeze(['Engagement', 'Reservation', 'Appointment', 'Booking']);

export function cleanBookingWord(value) {
    const word = String(value ?? '').trim();
    return BOOKING_WORD_PATTERN.test(word) ? word : DEFAULT_BOOKING_WORD;
}

// "Reservations" typed by mistake as the singular still reads right: a trailing plural "s" on a preset is folded back.
export function singularFromPlural(value) {
    const word = String(value ?? '').trim();
    const preset = BOOKING_WORD_PRESETS.find((item) => pluralWord(item).toLowerCase() === word.toLowerCase());
    return preset || word;
}

export function resolveCompanyWords(bookingWord) {
    const booking = cleanBookingWord(bookingWord);
    const bookings = pluralWord(booking);
    return Object.freeze({
        booking,
        bookings,
        bookingLower: lowerWord(booking),
        bookingsLower: lowerWord(bookings),
    });
}

export const DEFAULT_COMPANY_WORDS = resolveCompanyWords(null);

// Reads the staff word for the signed-in company. Any failure (including a database where migration 256 has not been
// applied yet) falls back to the default, so a missing column never breaks a screen.
export async function loadBookingWord(client, orgId) {
    if (!client || !orgId) return null;
    try {
        const { data, error } = await client.from('orgs').select('booking_word').eq('id', orgId).maybeSingle();
        if (error) return null;
        return data?.booking_word ?? null;
    } catch (error) {
        return null;
    }
}

// The one save. Staff word first (orgs.booking_word), then the client app's plural (org_settings.bookings_label).
// Resolves to { ok: true } or { ok: false, step: 'staff' | 'client', message }.
export async function saveBookingWord(client, orgId, word) {
    const booking = String(word ?? '').trim();
    if (!BOOKING_WORD_PATTERN.test(booking)) return { ok: false, step: 'staff', message: BOOKING_WORD_RULE };
    const plural = pluralWord(booking);
    if (!BOOKING_WORD_PATTERN.test(plural)) return { ok: false, step: 'client', message: BOOKING_WORD_RULE };
    try {
        const { data, error } = await client.from('orgs').update({ booking_word: booking }).eq('id', orgId).select('id');
        if (error) return { ok: false, step: 'staff', message: error.message };
        if (Array.isArray(data) && data.length === 0) {
            return { ok: false, step: 'staff', message: 'Only an Owner or Admin of this company can change it.' };
        }
    } catch (error) {
        return { ok: false, step: 'staff', message: error?.message || 'The word could not be saved.' };
    }
    try {
        const { error } = await client.from('org_settings')
            .upsert({ org_id: orgId, bookings_label: plural }, { onConflict: 'org_id' });
        if (error) return { ok: false, step: 'client', message: error.message };
    } catch (error) {
        return { ok: false, step: 'client', message: error?.message || 'The client app word could not be saved.' };
    }
    return { ok: true, booking, plural };
}
